import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Plus, Edit2, Trash2, X, Check, Loader, User } from 'lucide-react';

import { useFacility } from '../../context/FacilityContext';
import { formatDateTime } from '../../utils/datetime';
import customerNotesService from '../../services/customerNotesService';

/**
 * Customer Notes — operator-authored free-text log per customer.
 *
 * Mary's phone-support workflow: "called tenant 3pm, no answer, left VM",
 * "agreed to pay by Friday". Append-mostly with inline edit + soft delete.
 *
 * Edit-mode only — needs a persisted customerId to attach notes to. The
 * parent CustomerForm hides this tab on the New Customer view.
 *
 * Pagination: backend returns newest 100 non-deleted notes; we render them
 * all (typical tenant has 0-5 notes).
 *
 * Attachments: backend persists `attachment_url` + `attachment_filename`
 * so the data model is forward-compatible, but the v1 UI does NOT collect
 * a file. Wire to the existing /api/uploads/document endpoint in v2 once
 * the operator demand is real. (TODO v2)
 *
 * `onCountChange(n)` lets the parent show "Notes (N)" in the tab strip.
 */
const CustomerNotesSection = ({ customerId, onCountChange }) => {
  const { facilityId, currentFacility } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [draftBody, setDraftBody] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const refresh = useCallback(async () => {
    if (!facilityId || !customerId) return;
    setLoading(true);
    try {
      const res = await customerNotesService.list(facilityId, customerId);
      const list = Array.isArray(res.data) ? res.data : [];
      setNotes(list);
      if (typeof onCountChange === 'function') onCountChange(list.length);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [facilityId, customerId, onCountChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async () => {
    const body = draftBody.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      await customerNotesService.create(facilityId, customerId, { body });
      setDraftBody('');
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingBody(note.body || '');
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingBody('');
  };

  const saveEdit = async (noteId) => {
    const body = editingBody.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      await customerNotesService.update(facilityId, customerId, noteId, { body });
      setEditingNoteId(null);
      setEditingBody('');
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update note');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    setSubmitting(true);
    try {
      await customerNotesService.remove(facilityId, customerId, pendingDeleteId);
      setPendingDeleteId(null);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete note');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="rounded-xl border border-slate-200/80 p-4">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4 text-slate-400" />
          <span className="text-[13px] font-medium text-slate-700">Add a note</span>
        </div>
        <p className="text-[12px] text-slate-400 mb-3">
          Free-text log for calls, payment promises, follow-ups. Visible to
          all staff at this facility.
        </p>
        <textarea
          value={draftBody}
          onChange={(e) => setDraftBody(e.target.value)}
          rows={3}
          placeholder="e.g. Called tenant at 3pm. No answer; left VM about Unit 25 balance."
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!draftBody.trim() || submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? (
              <><Loader className="h-3.5 w-3.5 animate-spin" /> Adding...</>
            ) : (
              <><Plus className="h-3.5 w-3.5" /> Add note</>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200/80 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 text-[13px] text-slate-400">
          No notes yet. Add the first one above.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {notes.map((note) => (
            <li
              key={note.id}
              className="rounded-xl border border-slate-200/80 p-4 bg-white"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User className="h-3 w-3 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-slate-700 truncate">
                      {note.author_email || 'Unknown author'}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {formatDateTime(note.created_at, timeZone)}
                      {note.updated_at && (
                        <span className="ml-1">· edited {formatDateTime(note.updated_at, timeZone)}</span>
                      )}
                    </div>
                  </div>
                </div>
                {editingNoteId !== note.id && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(note)}
                      className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteId(note.id)}
                      className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {editingNoteId === note.id ? (
                <div>
                  <textarea
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={submitting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => saveEdit(note.id)}
                      disabled={!editingBody.trim() || submitting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Check className="h-3.5 w-3.5" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">
                  {note.body}
                </p>
              )}

              {note.attachment_url && (
                <a
                  href={note.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[12px] text-blue-600 hover:underline"
                >
                  {note.attachment_filename || 'Attachment'}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Delete confirmation modal */}
      {pendingDeleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
          onClick={() => setPendingDeleteId(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-sm w-full mx-4 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[15px] font-semibold text-slate-900 mb-1">Delete note?</h3>
            <p className="text-[13px] text-slate-500 mb-4">
              This note will be removed from the customer's timeline. The
              record is retained for audit but won't be visible to staff.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDeleteId(null)}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-[13px] font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? (
                  <><Loader className="h-3.5 w-3.5 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="h-3.5 w-3.5" /> Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerNotesSection;
